import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    vec3,
} from 'gl-matrix';
import { Vertex } from './slicemesh/Vertex';

export interface BoundingBoxBounds {
    min: vec3;
    max: vec3;
}

export class BoundingBox {
    public bounds?: BoundingBoxBounds;

    public constructor();

    public constructor(ref: BoundingBox);

    public constructor(point: ReadonlyVec3);

    public constructor(point: ReadonlyVec2);

    public constructor(min: ReadonlyVec3, max: ReadonlyVec3);

    public constructor(
        param1?: ReadonlyVec3 | ReadonlyVec2 | BoundingBox,
        param2?: ReadonlyVec3
    ) {
        if (param1 instanceof BoundingBox) {
            if (param1.bounds) {
                this.bounds = {
                    min: vec3.clone(param1.bounds.min),
                    max: vec3.clone(param1.bounds.max),
                };
            } else {
                this.bounds = undefined;
            }
        } else if (param1 !== undefined) {
            const min =
                param1.length === 3
                    ? vec3.clone(param1 as vec3)
                    : vec3.fromValues(param1[0], param1[1], 0);

            const max =
                param2 !== undefined ? vec3.clone(param2) : vec3.clone(min);

            // Validate that min <= max for all coordinates
            if (min[0] > max[0] || min[1] > max[1] || min[2] > max[2]) {
                throw new Error(
                    'Invalid bounding box: min must be less than or equal to max for all coordinates'
                );
            }

            this.bounds = { min, max };
        }
    }

    public get min(): vec3 | undefined {
        if (!this.bounds) {
            return undefined;
        }
        return vec3.clone(this.bounds.min);
    }

    public get max(): vec3 | undefined {
        if (!this.bounds) {
            return undefined;
        }
        return vec3.clone(this.bounds.max);
    }

    public get isEmpty() {
        return this.bounds === undefined;
    }

    public expandWithPoint(point: ReadonlyVec3): boolean;

    public expandWithPoint(x: ReadonlyVec3): boolean {
        if (!this.bounds) {
            this.bounds = {
                min: vec3.clone(x),
                max: vec3.clone(x),
            };
            return true;
        } else {
            const { min, max } = this.bounds;
            const oldMin = vec3.clone(min);
            const oldMax = vec3.clone(max);
            vec3.min(min, min, x);
            vec3.max(max, max, x);
            return !vec3.equals(oldMin, min) || !vec3.equals(oldMax, max);
        }
    }

    public expandWithPoints(collection: Iterable<ReadonlyVec3>): boolean {
        let expanded = false;
        for (const vertex of collection) {
            expanded = this.expandWithPoint(vertex) || expanded;
        }
        return expanded;
    }

    /**
     * Checks if a point is strictly inside the bounding box.
     * Points on the boundary (where any coordinate equals min or max) are considered outside.
     * @param point A vec3 point or individual x, y, z coordinates
     * @returns true if the point is strictly inside (not on boundary), false otherwise
     */
    public isPointInside(
        ...args: [point: ReadonlyVec3] | [x: number, y: number, z: number]
    ): boolean {
        if (!this.bounds) {
            return false;
        }

        const point =
            args.length === 1
                ? args[0]
                : vec3.fromValues(args[0], args[1], args[2]);

        const { min, max } = this.bounds;
        return (
            point[0] > min[0] &&
            point[0] < max[0] &&
            point[1] > min[1] &&
            point[1] < max[1] &&
            point[2] > min[2] &&
            point[2] < max[2]
        );
    }

    public contains(bb: BoundingBox): boolean {
        if (!this.bounds || !bb.bounds) {
            return false;
        } else {
            return (
                bb.bounds.min[0] >= this.bounds.min[0] &&
                bb.bounds.max[0] <= this.bounds.max[0] &&
                bb.bounds.min[1] >= this.bounds.min[1] &&
                bb.bounds.max[1] <= this.bounds.max[1] &&
                bb.bounds.min[2] >= this.bounds.min[2] &&
                bb.bounds.max[2] <= this.bounds.max[2]
            );
        }
    }

    public intersects(bb: BoundingBox): boolean {
        if (!this.bounds || !bb.bounds) {
            return false;
        }

        return (
            this.bounds.max[0] >= bb.bounds.min[0] &&
            this.bounds.min[0] <= bb.bounds.max[0] &&
            this.bounds.max[1] >= bb.bounds.min[1] &&
            this.bounds.min[1] <= bb.bounds.max[1] &&
            this.bounds.max[2] >= bb.bounds.min[2] &&
            this.bounds.min[2] <= bb.bounds.max[2]
        );
    }

    public addMargins(margin: number): BoundingBox;

    public addMargins(
        minMargin: ReadonlyVec3,
        maxMargin?: ReadonlyVec3
    ): BoundingBox;

    public addMargins(xMin: number, yMin: number, zMin: number): BoundingBox;

    public addMargins(
        xMin: number,
        yMin: number,
        zMin: number,
        xMax: number,
        yMax: number,
        zMax: number
    ): BoundingBox;

    public addMargins(
        xMin: number | ReadonlyVec3,
        yMin?: number | ReadonlyVec3,
        zMin?: number,
        xMax?: number,
        yMax?: number,
        zMax?: number
    ): BoundingBox {
        if (!this.bounds) {
            return this;
        }
        let minMargin: vec3;
        let maxMargin: vec3;
        if (typeof xMin !== 'number') {
            minMargin = vec3.clone(xMin);
            maxMargin =
                yMin !== undefined
                    ? vec3.clone(yMin as ReadonlyVec3)
                    : vec3.clone(minMargin);
        } else {
            if (yMin !== undefined) {
                minMargin = vec3.fromValues(
                    xMin,
                    yMin as number,
                    zMin as number
                );
                if (xMax !== undefined) {
                    maxMargin = vec3.fromValues(
                        xMax,
                        yMax as number,
                        zMax as number
                    );
                } else {
                    maxMargin = vec3.clone(minMargin);
                }
            } else {
                minMargin = vec3.fromValues(xMin, xMin, xMin);
                maxMargin = vec3.clone(minMargin);
            }
        }
        const { min, max } = this.bounds;
        vec3.subtract(min, min, minMargin);
        vec3.add(max, max, maxMargin);
        return this;
    }

    public set(other: BoundingBox): BoundingBox {
        if (other.bounds) {
            this.bounds = {
                min: vec3.clone(other.bounds.min),
                max: vec3.clone(other.bounds.max),
            };
        } else {
            this.bounds = undefined;
        }
        return this;
    }

    /**
     * @deprecated
     * Needs to be rewritten to consider RotatedBoundingBox
     * @param other
     * @returns new BoundingBox which covers both of the source boundingBoxes.
     */
    public static union(bb1: BoundingBox, bb2: BoundingBox): BoundingBox {
        if (!bb1.bounds) {
            if (!bb2.bounds) {
                return new BoundingBox();
            } else {
                return new BoundingBox(bb2);
            }
        } else if (!bb2.bounds) {
            return new BoundingBox(bb1);
        } else {
            const min = vec3.create();
            const max = vec3.create();
            vec3.min(min, bb1.bounds.min, bb2.bounds.min);
            vec3.max(max, bb1.bounds.max, bb2.bounds.max);
            return new BoundingBox(min, max);
        }
    }

    /**
     * Needs to be rewritten to consider RotatedBoundingBox
     * @deprecated
     * @param bb1
     * @param bb2
     * @returns
     */
    public static intersection(
        bb1: BoundingBox,
        bb2: BoundingBox
    ): BoundingBox {
        if (!bb1.bounds || !bb2.bounds) {
            return new BoundingBox();
        } else {
            const min = vec3.create();
            const max = vec3.create();
            vec3.max(min, bb1.bounds.min, bb2.bounds.min);
            vec3.min(max, bb1.bounds.max, bb2.bounds.max);

            // Check if boxes actually overlap (min should be <= max for all coordinates)
            if (min[0] > max[0] || min[1] > max[1] || min[2] > max[2]) {
                return new BoundingBox();
            }

            return new BoundingBox(min, max);
        }
    }

    /**
     * @returns The size of this bounding box.
     */
    public get size(): ReadonlyVec3 {
        const size = vec3.create();
        if (this.bounds) {
            vec3.subtract(size, this.bounds.max, this.bounds.min);
        }
        return size;
    }

    public get volume(): number {
        const size = this.size;
        return size[0] * size[1] * size[2];
    }

    /**
     * Method to get the center point of this BoundingBox.
     * Center point coordinates are (min + max) / 2 if min and max are defined, (0, 0, 0) otherwise.
     * Note that these coordinates are in an arbitrary space, not necessarily PatientSpace.
     */
    public get center(): ReadonlyVec3 {
        if (!this.bounds) {
            throw new Error('Cannot get center of an empty bounding box');
        } else {
            const size = vec3.add(
                vec3.create(),
                this.bounds.min,
                this.bounds.max
            );
            return vec3.scale(size, size, 0.5);
        }
    }

    translate(translation: ReadonlyVec3) {
        if (!this.bounds) {
            throw new Error('Cannot translate an empty bounding box');
        }
        const { min, max } = this.bounds;
        vec3.add(min, min, translation);
        vec3.add(max, max, translation);
    }

    scale(amount: number) {
        if (amount < 0) {
            throw new Error(
                'Trying to scale bounding box by a negative number'
            );
        }
        if (!this.bounds) {
            throw new Error('Cannot scale an empty bounding box');
        }
        const { min, max } = this.bounds;
        vec3.scale(min, min, amount);
        vec3.scale(max, max, amount);
    }

    /**
     * @returns Array of 8 vertices representing the {@link BoundingBox}'s corners.
     */
    public getVertices(transformation?: ReadonlyMat3 | ReadonlyMat4): vec3[] {
        if (!this.bounds) {
            return [];
        }

        const { min, max } = this.bounds;
        const vertices = [
            vec3.clone(min),
            vec3.fromValues(max[0], min[1], min[2]),
            vec3.fromValues(max[0], max[1], min[2]),
            vec3.fromValues(min[0], max[1], min[2]),
            vec3.fromValues(min[0], min[1], max[2]),
            vec3.fromValues(max[0], min[1], max[2]),
            vec3.clone(max),
            vec3.fromValues(min[0], max[1], max[2]),
        ];

        if (transformation) {
            if (transformation.length === 9) {
                for (const v of vertices) {
                    vec3.transformMat3(v, v, transformation);
                }
            } else {
                for (const v of vertices) {
                    vec3.transformMat4(v, v, transformation as ReadonlyMat4);
                }
            }
        }
        return vertices;
    }

    /**
     * Returns a new BoundingBox with vertices transformed by the given transformation matrix.
     * @param transformation The transformation matrix to apply (mat3 or mat4)
     * @returns A new BoundingBox containing the transformed vertices
     */
    public transform(transformation: ReadonlyMat3 | ReadonlyMat4): BoundingBox {
        if (!this.bounds) {
            return new BoundingBox();
        }

        const vertices = this.getVertices();
        const transformedVertices = vertices.map((v) =>
            transformation.length === 9
                ? vec3.transformMat3(
                      vec3.create(),
                      v,
                      transformation as ReadonlyMat3
                  )
                : vec3.transformMat4(
                      vec3.create(),
                      v,
                      transformation as ReadonlyMat4
                  )
        );

        const result = new BoundingBox();
        result.expandWithPoints(transformedVertices);
        return result;
    }

    /**
     * Method to get the 8 {@link Vertex Vertices} of the {@link BoundingBox} in PatientSpace along with texture coordinates.\
     * @returns Array of 8 {@link Vertex Vertices} representing the {@link BoundingBox}'s corners.
     */
    public getVerticesWithTexture(
        transformation?: ReadonlyMat3 | ReadonlyMat4
    ): Vertex[] {
        if (!this.bounds) {
            return [];
        }
        const { min, max } = this.bounds;
        // texture coordinates are in the middle of the slice
        const textureKcoord = 0.5 / this.size[2];
        const coordinates = [
            // bottom
            [min, vec3.fromValues(0, 0, textureKcoord)],
            [
                vec3.fromValues(min[0], max[1], min[2]),
                vec3.fromValues(0, 1, textureKcoord),
            ],
            [
                vec3.fromValues(max[0], max[1], min[2]),
                vec3.fromValues(1, 1, textureKcoord),
            ],
            [
                vec3.fromValues(max[0], min[1], min[2]),
                vec3.fromValues(1, 0, textureKcoord),
            ],
            // top
            [
                vec3.fromValues(min[0], min[1], max[2]),
                vec3.fromValues(0, 0, 1 - textureKcoord),
            ],
            [
                vec3.fromValues(min[0], max[1], max[2]),
                vec3.fromValues(0, 1, 1 - textureKcoord),
            ],
            [
                vec3.fromValues(max[0], max[1], max[2]),
                vec3.fromValues(1, 1, 1 - textureKcoord),
            ],
            [
                vec3.fromValues(max[0], min[1], max[2]),
                vec3.fromValues(1, 0, 1 - textureKcoord),
            ],
        ];
        let vertices: Vertex[];
        if (transformation) {
            vertices = coordinates.map((coords: Array<vec3>) => {
                return new Vertex(coords[0], coords[1]).transform(
                    transformation
                );
            });
        } else {
            vertices = coordinates.map((coords: Array<vec3>) => {
                return new Vertex(coords[0], coords[1]);
            });
        }
        return vertices;
    }

    /**
     * @override
     *
     * Method to get string presentation of a bounding box
     */
    public toString(): string {
        if (this.bounds) {
            return (
                this.bounds.min.toString() + ' to ' + this.bounds.max.toString()
            );
        } else {
            return '';
        }
    }
}
